import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileStreamUrl } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import TrackCard from "../components/TrackCard";
import ClaimProfileModal from "../components/ClaimProfileModal";
import { MapPin, UserPlus, UserMinus, Instagram, Youtube, Music, ShieldCheck } from "lucide-react";

export const ArtistProfilePage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [artist, setArtist] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [following, setFollowing] = useState(false);
  const [claimable, setClaimable] = useState(false);
  const [showClaim, setShowClaim] = useState(false);

  const load = async () => {
    const [a, t, f, c] = await Promise.all([
      api.get(`/users/${id}`),
      api.get("/tracks", { params: { artist_id: id } }),
      api.get(`/artists/${id}/is-following`).catch(() => ({ data: { following: false } })),
      api.get(`/artists/${id}/claim-status`).catch(() => ({ data: { claimable: false } })),
    ]);
    setArtist(a.data); setTracks(t.data); setFollowing(f.data.following);
    setClaimable(c.data.claimable);
  };

  useEffect(() => { load(); }, [id]);

  const toggleFollow = async () => {
    if (!user) { window.location.href = "/login"; return; }
    const { data } = await api.post(`/artists/${id}/follow`);
    setFollowing(data.following);
    // refresh artist counts
    api.get(`/users/${id}`).then(({ data }) => setArtist(data));
  };

  if (!artist) return <div className="p-12 text-white/60">Chargement...</div>;

  const links = artist.social_links || {};

  return (
    <div className="min-h-screen" data-testid="artist-profile-page">
      {/* Banner */}
      <div className="relative h-64 md:h-80 bg-gradient-to-br from-brand-red/40 via-brand-orange/30 to-brand-yellow/20 overflow-hidden">
        {artist.picture && (
          <img src={artist.picture} className="absolute inset-0 w-full h-full object-cover opacity-40" alt="" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/50 to-transparent" />
      </div>

      <div className="px-6 md:px-12 max-w-7xl mx-auto -mt-20 relative">
        <div className="flex flex-col md:flex-row md:items-end gap-6 mb-10">
          <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden bg-gradient-to-br from-brand-red to-brand-yellow ring-4 ring-brand-dark shrink-0">
            {artist.picture ? (
              <img src={artist.picture} alt={artist.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white font-black text-5xl">
                {artist.name?.[0]?.toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {artist.is_premium && <span className="text-[10px] tracking-[0.25em] uppercase text-brand-yellow font-bold">Premium</span>}
              {artist.is_verified && <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-sky-400 font-bold"><svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 2.4 3.4-.8.9 3.3 3.3.9-.8 3.4L23.6 14l-2.4 2.4.8 3.4-3.3.9-.9 3.3-3.4-.8L12 25.6l-2.4-2.4-3.4.8-.9-3.3-3.3-.9.8-3.4L.4 14l2.4-2.4-.8-3.4 3.3-.9.9-3.3 3.4.8L12 2z"/></svg>Vérifié</span>}
              {artist.is_featured && <span className="text-[10px] tracking-widest uppercase text-brand-red font-bold">En vedette</span>}
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter mb-2">{artist.name}</h1>
            <div className="flex flex-wrap items-center gap-3 text-white/70 text-sm">
              {artist.country && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {artist.city ? `${artist.city}, ${artist.country}` : artist.country}</span>}
              {artist.genre && <span className="tag-pill">{artist.genre}</span>}
              <span>{artist.followers_count} followers</span>
              <span>{tracks.length} morceaux</span>
            </div>
            {artist.bio && <p className="mt-4 text-white/80 max-w-2xl">{artist.bio}</p>}

            {claimable && (
              <div className="mt-4 max-w-2xl bg-gradient-to-r from-brand-red/10 to-brand-yellow/10 border border-brand-red/30 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <ShieldCheck size={28} className="text-brand-red shrink-0" />
                <div className="flex-1 text-sm">
                  <p className="font-semibold mb-0.5">Vous êtes {artist.name} ?</p>
                  <p className="text-white/60 text-xs">Ce profil a été créé par l'équipe Voamada. Réclamez-le en 1 minute pour en prendre le contrôle total.</p>
                </div>
                <button
                  onClick={() => setShowClaim(true)}
                  className="btn-primary text-sm inline-flex items-center gap-2 shrink-0"
                  data-testid="claim-profile-btn"
                >
                  <ShieldCheck size={14} /> Réclamer
                </button>
              </div>
            )}

            <div className="mt-5 flex items-center gap-3 flex-wrap">
              {user?.user_id !== artist.user_id && (
                <button
                  onClick={toggleFollow}
                  className={following ? "btn-ghost inline-flex items-center gap-2" : "btn-primary inline-flex items-center gap-2"}
                  data-testid="follow-artist-btn"
                >
                  {following ? <><UserMinus size={16} /> Suivi</> : <><UserPlus size={16} /> Suivre</>}
                </button>
              )}
              {links.youtube && <a href={links.youtube} target="_blank" rel="noreferrer" className="text-white/60 hover:text-brand-red" data-testid="social-youtube"><Youtube size={22} /></a>}
              {links.spotify && <a href={links.spotify} target="_blank" rel="noreferrer" className="text-white/60 hover:text-brand-red" data-testid="social-spotify"><Music size={22} /></a>}
              {links.instagram && <a href={links.instagram} target="_blank" rel="noreferrer" className="text-white/60 hover:text-brand-red" data-testid="social-instagram"><Instagram size={22} /></a>}
              {links.tiktok && <a href={links.tiktok} target="_blank" rel="noreferrer" className="text-white/60 hover:text-brand-red text-sm font-semibold" data-testid="social-tiktok">TikTok</a>}
            </div>
          </div>
        </div>

        <h2 className="font-display text-2xl md:text-3xl font-black mb-6">Morceaux</h2>
        {tracks.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center mb-20">
            <p className="text-white/60">Aucun morceau publié pour l'instant.</p>
            {user?.user_id === artist.user_id && (
              <Link to="/upload" className="btn-primary inline-flex items-center gap-2 mt-4">Publier mon premier morceau</Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-20 stagger">
            {tracks.map((t) => <TrackCard key={t.track_id} track={t} list={tracks} />)}
          </div>
        )}
      </div>

      <ClaimProfileModal artist={artist} open={showClaim} onClose={() => setShowClaim(false)} />
    </div>
  );
};

export default ArtistProfilePage;
